/**
 * Frappe-style activity timeline for any record-detail page.
 *
 * Drop into a detail page with:
 *
 *     <ActivityTimeline
 *       entityType="employees.Employee"
 *       entityId={employee.id}
 *       entityLabel={employee.full_name}
 *     />
 *
 * The component talks to `GET /api/v1/core/audit/timeline/` and the
 * comment composer hits `POST /api/v1/core/audit/comment/`.
 */
import { useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Dot,
  Flag,
  Heart,
  MessageSquare,
  Paperclip,
  RotateCcw,
  Trash2,
  User,
  type LucideIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ErrorMessage } from "@/components/ui/error-message";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/toast";
import { api } from "@/lib/api";
import { formatRelative } from "@/lib/format";
import type { ActivityEntry, ActivityTimelineResponse } from "@/lib/types";
import { cn } from "@/lib/utils";

interface ActivityTimelineProps {
  entityType: string;
  entityId: string | number;
  entityLabel?: string;
  className?: string;
  allowComments?: boolean;
}

const ICON_MAP: Record<string, LucideIcon> = {
  dot: Dot,
  trash: Trash2,
  "rotate-ccw": RotateCcw,
  paperclip: Paperclip,
  "message-square": MessageSquare,
  heart: Heart,
  user: User,
  flag: Flag,
};

export function ActivityTimeline({
  entityType,
  entityId,
  entityLabel,
  className,
  allowComments = true,
}: ActivityTimelineProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showAll, setShowAll] = useState(false);
  const [draft, setDraft] = useState("");

  const queryKey = ["activity-timeline", entityType, String(entityId)];

  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey,
      queryFn: async ({ pageParam }) => {
        const params = new URLSearchParams({
          entity_type: entityType,
          entity_id: String(entityId),
        });
        if (pageParam) params.set("before", pageParam as string);
        return api.get<ActivityTimelineResponse>(`/core/audit/timeline/?${params}`);
      },
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => lastPage.next_before,
    });

  const postComment = useMutation({
    mutationFn: async (text: string) =>
      api.post<ActivityEntry>("/core/audit/comment/", {
        entity_type: entityType,
        entity_id: String(entityId),
        text,
      }),
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey });
      toast.success("Comment posted");
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to post comment"),
  });

  const entries: ActivityEntry[] = (data?.pages ?? []).flatMap(
    (p) => p.results ?? [],
  );
  const visible = showAll ? entries : entries.filter((e) => e.signal);

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Activity</CardTitle>
        <CardDescription>
          {entityLabel ? `History of changes on ${entityLabel}` : "History of changes on this record"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {allowComments && (
          <form
            className="space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              const text = draft.trim();
              if (!text) return;
              postComment.mutate(text);
            }}
          >
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Add a comment…"
              rows={2}
              className="resize-none"
            />
            <div className="flex justify-end">
              <Button
                type="submit"
                size="sm"
                disabled={!draft.trim() || postComment.isPending}
              >
                {postComment.isPending ? "Posting…" : "Post"}
              </Button>
            </div>
          </form>
        )}

        {isLoading && (
          <div className="space-y-3" aria-label="Loading activity">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-3/4" />
          </div>
        )}

        {isError && (
          <ErrorMessage error={error} fallback="Could not load activity." />
        )}

        {!isLoading && !isError && entries.length === 0 && (
          <p className="text-sm text-muted-foreground">No activity yet.</p>
        )}

        {!isLoading && !isError && visible.length > 0 && (
          <ol className="relative space-y-4 border-l border-slate-200 pl-5">
            {visible.map((entry) => (
              <TimelineRow key={entry.id} entry={entry} />
            ))}
          </ol>
        )}

        <div className="flex items-center justify-between pt-2 text-sm">
          {entries.length > visible.length && !showAll ? (
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => setShowAll(true)}
            >
              Show all activity ({entries.length - visible.length} hidden)
            </button>
          ) : (
            <span />
          )}
          {hasNextPage && (
            <Button
              variant="ghost"
              size="sm"
              disabled={isFetchingNextPage}
              onClick={() => fetchNextPage()}
            >
              {isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface TimelineRowProps {
  entry: ActivityEntry;
}

function TimelineRow({ entry }: TimelineRowProps) {
  const Icon = ICON_MAP[entry.icon] ?? Dot;
  const isComment = entry.action_code === "activity.comment";
  return (
    <li className="relative">
      <span
        className={cn(
          "absolute -left-[1.625rem] flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500",
          isComment && "text-primary",
        )}
        aria-hidden
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="flex flex-wrap items-baseline gap-x-2 text-sm leading-snug">
        <span className="font-medium text-slate-900">{entry.actor_label}</span>
        <span className="text-slate-600">{entry.verb}</span>
        <span className="text-xs text-muted-foreground">
          · {formatRelative(entry.occurred_at)}
        </span>
      </div>
      {isComment && entry.body && (
        <p className="mt-1 whitespace-pre-wrap rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
          {entry.body}
        </p>
      )}
    </li>
  );
}
